import { ScaffoldDropZone } from "../../scaffold";
import { type EditorDefns, type TlfViewModeConfig } from "../../utility/interface";
import { CaseWhenCondition } from "../ars_ldm";
import { type AnnotationVariable } from "../contexts/interface";
import { ArsTableRow, ArsTableScaffoldModel, ArsTextJustification } from "../table";

export type ArsListingTextAlignment = "left" | "center" | "right" | "decimal" | "default";
/**
 * Identifies how a variable is used within an ARS listing column.
 */
export enum ArsListingVariableEnum {
  DisplayOnly = "displayOnly",
  Group = "group",
  HiddenSortAscending = "hidden-sort-ascending",
  HiddenSortDescending = "hidden-sort-descending",
}

export const ARSL_DEFAULT_COLUMN_WIDTH = 200;

export interface ArsListingVariable {
  /** Unique identifier for the variable in the column */
  id: string;
  /** Dataset associated with the variable */
  dataset: string;
  /** Variable name */
  variable: string;
  /** Variable type */
  variableType: ArsListingVariableEnum;
}

/**
 * A selected listing column instance.
 */
export interface ArsListingColumn<T = string> extends Record<
  string,
  string | number | boolean | undefined | T | T[] | ArsListingVariable[] | CaseWhenCondition<T>
> {
  /** Unique identifier for the column. */
  id: string;
  /** Column width in pixels */
  columnWidth: number;
  /** Text alignment for body row cells */
  bodyTextAlignment?: ArsTextJustification;
  /** Decimal align percent */
  decimalAlignPercent?: number;
  /** Display pattern for the column */
  displayPattern: T[];
  // displayPattern: T | ConditionalDisplayValue<T>[];
  /** Header text for the column */
  headerText: T;
  /** Height of the header row in pixels */
  headerHeight?: number;
  /** Text alignment for header row cells */
  headerTextAlignment?: ArsTextJustification;
  /** Result pattern for the column, used in calculations */
  resultPattern: string;
  /** Variables associated with the column */
  variables: ArsListingVariable[];
  /** Case When condition for the column */
  caseWhenCondition?: CaseWhenCondition<T>;
}

/**
 * A single body row definition for ArsListing, controlling height and display mode.
 */
export interface ArsListingRow {
  /** Unique identifier for the row. */
  id: string;
  /** Height of the row in pixels. */
  rowHeight: number;
  /** When true the row displays an ellipsis instead of the column display pattern. */
  ellipsis: boolean;
}

/**
 * Body data structure for ArsListing, containing column definitions and row values.
 */
export interface ArsListingBody<T = string> {
  /** Unique identifier for the body data. */
  id: string;
  /** Column definitions for the listing. */
  arsListingColumns: ArsListingColumn<T>[];
  /** Row values for the listing. */
  arsBodyRows: ArsListingRow[];
  /** Reference ID to the DataSubset from the hierarchy level. Undefined (omitted) indicates no data subset is selected. */
  dataSubsetId?: string;
}

/**
 * Settings for ArsListing, such as display options and configurations.
 */
export interface ArsListingSettings<T = string> {
  /** Listing scaffold identifier used by the table control. */
  id: string;
  /** When true, disables listing interactions. */
  disabled: boolean;
  /** Horizontal justification of the scaffold table within its container */
  justification?: "left" | "center" | "right";
  /** Editor props used by cell editors */
  editorProps: EditorDefns<T>;
  /** Width of the draggable sidebar drawer. */
  sidebarWidth: string;
  /** Optional CSS styles for the sidebar container */
  sidebarStyle?: React.CSSProperties;
  /** Optional container style */
  style?: React.CSSProperties;
  /** TLF view mode controlling how cell data is displayed. */
  tlfViewMode: TlfViewModeConfig;
  /** Text used to filter the variable sidebar by name or description. */
  variableFilter: string;
  /** Column ID currently being viewed in the column properties panel. */
  viewColumnPropertiesId?: string;
}

/**
 * State shape for ArsListing, containing body data, datasets and change tracking.
 */
export interface ArsListingState<T = string> extends Record<
  string,
  | Set<string>
  | ArsListingBody<T>
  | ArsListingSettings<T>
  | boolean
  | ArsTableRow<T>[]
  | ArsTableScaffoldModel<T>
  | null
> {
  /** Body data for the listing, including columns and rows. */
  bodyData: ArsListingBody<T>;
  /** Calculated scaffold rows for the listing. */
  calculatedTableRows: ArsTableRow<T>[];
  /** Indicates if the listing has been modified. */
  changed: boolean;
  /** Data sets associated with the listing. */
  datasets: Set<string>;
  /** Calculated scaffold model for the listing. */
  scaffoldModel: ArsTableScaffoldModel<T> | null;
  /** Settings for the listing */
  settings: ArsListingSettings<T>;
}

/**
 * Column placement action derived from a drop zone.
 */
export type HeaderDropAction = "insert-before" | "insert-after" | "replace";

/**
 * Header row identifier for ArsListing scaffold.
 */
export const HEADER_ROW_ID = "ars-listing-header-row";

/**
 * Empty column identifier shown before any variables are dropped.
 */
export const EMPTY_HEADER_COL_ID = "ars-listing-empty-header-col";

/**
 * Action types for ArsListing reducer.
 */
export const ARSL_ADD_BLANK_COLUMN = "ARSL_ADD_BLANK_COLUMN";
export const ARSL_ADD_ROW = "ARSL_ADD_ROW";
export const ARSL_ADD_VARIABLES_TO_COLUMN = "ARSL_ADD_VARIABLES_TO_COLUMN";
export const ARSL_ALIGN_TEXT = "ARSL_ALIGN_TEXT";
export const ARSL_APPLY_DROPPED_COLUMNS = "ARSL_APPLY_DROPPED_COLUMNS";
export const ARSL_APPLY_DROPPED_SORT_COLUMNS = "ARSL_APPLY_DROPPED_SORT_COLUMNS";
export const ARSL_CELL_UPDATE = "ARSL_CELL_UPDATE";
export const ARSL_CHANGE_SENT = "ARSL_CHANGE_SENT";
export const ARSL_COLUMN_UPDATE = "ARSL_COLUMN_UPDATE";
export const ARSL_LOAD_DATA = "ARSL_LOAD_DATA";
export const ARSL_REMOVE_COLUMN = "ARSL_REMOVE_COLUMN";
export const ARSL_REMOVE_ROW = "ARSL_REMOVE_ROW";
export const ARSL_SET_COLUMNS = "ARSL_SET_COLUMNS";
export const ARSL_SET_ROW_HEIGHT = "ARSL_SET_ROW_HEIGHT";
export const ARSL_SET_TLF_VIEW_MODE = "ARSL_SET_TLF_VIEW_MODE";
export const ARSL_SET_VARIABLE_FILTER = "ARSL_SET_VARIABLE_FILTER";
export const ARSL_SET_VIEW_COLUMN_PROPERTIES_ID = "ARSL_SET_VIEW_COLUMN_PROPERTIES_ID";
export const ARSL_UPDATE_SCAFFOLD = "ARSL_UPDATE_SCAFFOLD";

export interface ArslActionAddBlankColumn {
  operation: typeof ARSL_ADD_BLANK_COLUMN;
  colIx: number;
  colId: string;
}

export interface ArslActionAddRow {
  operation: typeof ARSL_ADD_ROW;
  rowId: string;
  type: "default" | "ellipsis";
}

export interface ArslActionAddVariablesToColumn {
  operation: typeof ARSL_ADD_VARIABLES_TO_COLUMN;
  colId: string;
  variables: AnnotationVariable[];
}

/**
 * Sets text alignment for a target column.
 */
export interface ArslActionAlignText {
  operation: typeof ARSL_ALIGN_TEXT;
  colId: string;
  alignment: ArsTextJustification;
  isHeader: boolean;
}

/**
 * Applies dropped columns to the listing at the target column position.
 */
export interface ArslActionApplyDroppedColumns<T = string> {
  operation: typeof ARSL_APPLY_DROPPED_COLUMNS;
  colId: string;
  columns: ArsListingColumn<T>[];
  dropAction: HeaderDropAction;
}

/**
 * Applies dropped columns to the listing at the target column position.
 */
export interface ArslActionApplyDroppedSortColumns {
  operation: typeof ARSL_APPLY_DROPPED_SORT_COLUMNS;
  colId: string;
  jsonPayload: string;
  variableIndex: number;
}

export interface ArslActionCellUpdate<T = string> {
  operation: typeof ARSL_CELL_UPDATE;
  rowId: string;
  colId: string;
  value: T;
}

export interface ArslActionChange {
  operation: typeof ARSL_CHANGE_SENT;
}

export interface ArslActionColumnUpdate<T = string> {
  operation: typeof ARSL_COLUMN_UPDATE;
  colId: string;
  column: Partial<ArsListingColumn<T>>;
}

export interface ArslActionLoadData<T> {
  operation: typeof ARSL_LOAD_DATA;
  bodyData: ArsListingBody<T>;
}

/**
 * Removes a selected listing column.
 */
export interface ArslActionRemoveColumn {
  operation: typeof ARSL_REMOVE_COLUMN;
  colId: string;
}

export interface ArslActionRemoveRow {
  operation: typeof ARSL_REMOVE_ROW;
  rowId: string;
}

/**
 * Replaces listing columns with an updated set.
 */
export interface ArslActionSetColumns<T = string> {
  operation: typeof ARSL_SET_COLUMNS;
  columns: ArsListingColumn<T>[];
}

/**
 * Updates row height for a selected body row.
 */
export interface ArslActionSetRowHeight {
  operation: typeof ARSL_SET_ROW_HEIGHT;
  rowId: string;
  rowHeight: number;
}

/**
 * Sets the TLF view mode in settings.
 */
export interface ArslActionSetTlfViewMode {
  operation: typeof ARSL_SET_TLF_VIEW_MODE;
  tlfViewMode: TlfViewModeConfig;
}

/**
 * Sets the variable filter text used to search variables by name or description in the sidebar.
 */
export interface ArslActionSetVariableFilter {
  operation: typeof ARSL_SET_VARIABLE_FILTER;
  variableFilter: string;
}

/**
 * Update the scaffold with new calculated table rows.
 */
export interface ArslActionUpdateScaffold<T = string> {
  operation: typeof ARSL_UPDATE_SCAFFOLD;
  calculatedTableRows: ArsTableRow<T>[];
}

/**
 * Set the column id for the column properties view.
 */
export interface ArslActionSetViewColumnPropertiesId {
  operation: typeof ARSL_SET_VIEW_COLUMN_PROPERTIES_ID;
  colId?: string;
}

export type ArsListingAction<T = string> =
  | ArslActionAddBlankColumn
  | ArslActionAddRow
  | ArslActionAddVariablesToColumn
  | ArslActionAlignText
  | ArslActionApplyDroppedColumns<T>
  | ArslActionApplyDroppedSortColumns
  | ArslActionCellUpdate<T>
  | ArslActionChange
  | ArslActionColumnUpdate<T>
  | ArslActionLoadData<T>
  | ArslActionRemoveColumn
  | ArslActionRemoveRow
  | ArslActionSetColumns<T>
  | ArslActionSetRowHeight
  | ArslActionSetTlfViewMode
  | ArslActionSetVariableFilter
  | ArslActionSetViewColumnPropertiesId
  | ArslActionUpdateScaffold<T>;

/**
 * Zones that map to an insert-before action in the header.
 */
export const LEFT_HEADER_ZONES: ScaffoldDropZone[] = [
  ScaffoldDropZone.TopLeft,
  ScaffoldDropZone.LeftEdgeTop,
  ScaffoldDropZone.LeftEdgeBottom,
];

/**
 * Zones that map to a replace action in the header.
 */
export const CENTRE_HEADER_ZONES: ScaffoldDropZone[] = [
  ScaffoldDropZone.TopEdgeLeft,
  ScaffoldDropZone.TopEdgeRight,
  ScaffoldDropZone.CentreTopLeft,
  ScaffoldDropZone.CentreTopRight,
  ScaffoldDropZone.CentreBottomLeft,
  ScaffoldDropZone.CentreBottomRight,
];

/**
 * Zones that map to an insert-after action in the header.
 */
export const RIGHT_HEADER_ZONES: ScaffoldDropZone[] = [
  ScaffoldDropZone.TopRight,
  ScaffoldDropZone.RightEdgeTop,
  ScaffoldDropZone.RightEdgeBottom,
];

/**
 * Zones that map to an add-variables-to-column action in the header.
 */
export const BOTTOM_HEADER_ZONES: ScaffoldDropZone[] = [
  ScaffoldDropZone.BottomLeft,
  ScaffoldDropZone.BottomEdgeLeft,
  ScaffoldDropZone.BottomEdgeRight,
  ScaffoldDropZone.BottomRight,
];
